import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category colors for visual variety
const categoryColors = {
  'Entretien': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', accent: 'bg-emerald-500', hover: 'hover:border-emerald-400 hover:shadow-emerald-100' },
  'Installation': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', accent: 'bg-blue-500', hover: 'hover:border-blue-400 hover:shadow-blue-100' },
  'Conseils pratiques': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', accent: 'bg-amber-500', hover: 'hover:border-amber-400 hover:shadow-amber-100' },
  'Guide d\'achat': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', accent: 'bg-purple-500', hover: 'hover:border-purple-400 hover:shadow-purple-100' },
};

const getColorClasses = (category) => {
  return categoryColors[category] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', accent: 'bg-slate-500', hover: 'hover:border-slate-400 hover:shadow-slate-100' };
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category))];
  const filteredPosts = activeCategory === 'all' ? posts : posts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="blog-page">
      <SEO 
        title="Conseils d'experts Spa - Guide Complet | Atlantique Spa Boutique"
        description="Conseils d'expert pour bien choisir, installer et entretenir votre spa. Guides pratiques par Atlantique Spa Boutique, technicien spécialiste Grand Ouest."
        canonical="/blog"
      />
      
      {/* Breadcrumb */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Conseils & Blog</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Header with photo background */}
      <div className="relative h-72 lg:h-80 overflow-hidden">
        <img 
          src="/images/terrain/spa-terrasse-bois.jpg" 
          alt="Spa sur terrasse en bois"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#1E3A5F]/85 to-[#1E3A5F]/60"></div>
        
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#F97316]" />
              <span className="text-[#F97316] font-medium text-sm uppercase tracking-wide">Blog & Conseils</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              Conseils d'experts pour bien choisir, installer et entretenir son spa
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl">
              Nos guides pratiques pour vous accompagner dans votre projet
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Category Filters - Colorful pills */}
        <div className="flex flex-wrap gap-2 mb-10">
          <Button
            variant={activeCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveCategory('all')}
            className={activeCategory === 'all' 
              ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] shadow-lg shadow-blue-200' 
              : 'border-slate-300 hover:border-[#1E3A5F] hover:bg-slate-50'}
          >
            Tous les articles
          </Button>
          {categories.filter(c => c !== 'all').map(cat => {
            const colors = getColorClasses(cat);
            const isActive = activeCategory === cat;
            return (
              <Button
                key={cat}
                variant="outline"
                size="sm"
                onClick={() => setActiveCategory(cat)}
                className={isActive 
                  ? `${colors.bg} ${colors.border} ${colors.text} border-2 shadow-md` 
                  : `border-slate-300 hover:${colors.bg} hover:${colors.border} hover:${colors.text}`}
              >
                {cat}
              </Button>
            );
          })}
        </div>

        {/* Blog Posts - Colorful Cards */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-5">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse shadow-lg">
                <div className="h-4 bg-slate-200 rounded-full w-24 mb-4" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
            <p className="text-slate-600">Aucun article dans cette catégorie.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {filteredPosts.map((post, index) => {
              const colors = getColorClasses(post.category);
              return (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <article 
                    className={`relative bg-white rounded-2xl p-6 md:p-8 h-full transition-all duration-300 shadow-lg shadow-slate-200/50 border-2 border-transparent ${colors.hover} hover:shadow-xl hover:-translate-y-1`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* Colored accent bar */}
                    <div className={`absolute top-0 left-6 right-6 h-1 ${colors.accent} rounded-b-full`}></div>
                    
                    {/* Category Badge */}
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${colors.text} ${colors.bg} px-3 py-1.5 rounded-full mb-5 border ${colors.border}`}>
                      <span className={`w-2 h-2 rounded-full ${colors.accent}`}></span>
                      {post.category}
                    </span>
                    
                    {/* Title */}
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-[#1E3A5F] transition-colors duration-300">
                      {post.title}
                    </h2>
                    
                    {/* Excerpt - 1 line max */}
                    {post.excerpt && (
                      <p className="text-slate-500 text-sm line-clamp-2 mb-5">
                        {post.excerpt}
                      </p>
                    )}
                    
                    {/* CTA Button */}
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-2 font-semibold text-sm ${colors.text} group-hover:gap-3 transition-all duration-300`}>
                        Lire l'article
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA Section - More dynamic */}
        <div className="mt-16 relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F97316] via-[#EA580C] to-[#DC2626]"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-50"></div>
          
          <div className="relative p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Un projet de spa ? Parlons-en !
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Notre technicien expert vous conseille gratuitement pour choisir le spa adapté à vos besoins
            </p>
            <Link to="/devis">
              <Button size="lg" className="bg-white text-[#EA580C] hover:bg-slate-100 font-bold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                Demander un devis gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
